import React from 'react';
import { 
  CheckCircle2, AlertTriangle, Save, Download, Loader2, GitCompare, Users, FileText, ChevronRight
} from 'lucide-react';
import { CalculationDetails } from './CalculationDetails';

// Default sections that the BCB will auto-generate from solution_data.
// Kept aligned with /api/business-case-builder/generate so the
// preview matches what the user actually lands into.
const BCB_DEFAULT_SECTIONS = [
  { title: 'Executive Summary', desc: 'High-level case summary tailored to the CFO audience' },
  { title: 'Strategic Context', desc: 'Why this learning investment matters for the business' },
  { title: 'Investment & Expected Return', desc: 'Total investment, projected returns, payback period' },
  { title: 'Methodology & Assumptions', desc: 'How ROE was calculated and which baselines were used' },
  { title: 'Recommendation', desc: 'Recommended next actions and decision required' },
];

export const ROIResults = ({ results, onSave, onExport, saving, onCompare, comparing, selectedCohort, constraint }) => {
  if (!results) return null;
  
  const { kpi, financial_summary, interventions, lei_measures } = results;
  const isPositiveROI = financial_summary.roi_percent > 0;
  // Expected Value Recovery — the slice of the active Mission Control
  // constraint leak this run's KPI uplift wins back (capped at the
  // leak itself). Same formula as Compare Scenarios' ranking row.
  const evr = constraint && Number.isFinite(Number(constraint.leak))
    ? Math.min(financial_summary.kpi_uplift_percent, Number(constraint.leak))
    : null;
  
  return (
    <div className="space-y-6">
      {/* Cohort Banner - if cohort selected */}
      {selectedCohort && (
        <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Users className="text-teal-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Target Cohort: {selectedCohort.name}</h3>
              <p className="text-sm text-teal-600">
                This analysis will be tracked against actual performance data for this cohort in Gap Analysis
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* ROI Summary */}
      <div className={`p-6 rounded-xl ${isPositiveROI ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-rose-50 border-2 border-rose-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">ROI Analysis Results</h3>
          {isPositiveROI ? (
            <CheckCircle2 className="text-emerald-600" size={32} />
          ) : (
            <AlertTriangle className="text-rose-600" size={32} />
          )}
        </div>
        
        <div className={`grid grid-cols-2 md:grid-cols-4 ${evr != null ? 'lg:grid-cols-5' : ''} gap-4`}>
          <div className="bg-white p-4 rounded-lg">
            <span className="text-sm text-gray-500">Total Investment</span>
            <p className="text-2xl font-bold text-gray-900">${financial_summary.total_investment.toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <span className="text-sm text-gray-500">Expected Impact</span>
            <p className="text-2xl font-bold text-blue-600">${financial_summary.total_financial_impact.toLocaleString()}</p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <span className="text-sm text-gray-500">Net Benefit</span>
            <p className={`text-2xl font-bold ${financial_summary.net_benefit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              ${financial_summary.net_benefit.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <span className="text-sm text-gray-500">ROI</span>
            <p className={`text-2xl font-bold ${isPositiveROI ? 'text-emerald-600' : 'text-rose-600'}`}>
              {financial_summary.roi_multiplier.toFixed(1)}x
            </p>
          </div>
          {evr != null && (
            <div className="bg-white p-4 rounded-lg ring-1 ring-orange-200" data-testid="sf-results-evr">
              <span className="text-sm text-gray-500">Expected Value Recovery</span>
              <p className="text-2xl font-bold text-emerald-600">+{evr.toFixed(1)}%</p>
              <span className="text-[10px] font-semibold text-orange-600">of {constraint.label} {constraint.leak}% leak</span>
            </div>
          )}
        </div>
        
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span className="text-gray-600">
            <strong>{financial_summary.roi_percent > 0 ? '+' : ''}{financial_summary.roi_percent.toFixed(0)}%</strong> return on investment
          </span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">
            <strong>{financial_summary.payback_months.toFixed(1)}</strong> month payback period
          </span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">
            <strong>{financial_summary.kpi_uplift_percent.toFixed(2)}%</strong> KPI improvement
          </span>
        </div>
      </div>
      
      {/* Interventions Breakdown */}
      <div className="bg-white rounded-xl border p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Intervention Breakdown</h4>
        <div className="space-y-3">
          {interventions.map((int, idx) => (
            <div key={int.name || `int-${idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-900">{int.intervention_name}</span>
                <span className="text-sm text-gray-500 ml-2">via {int.modality_name}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">{int.target_audience_size} people</span>
                <span className="text-gray-600">${int.total_cost.toLocaleString()}</span>
                <span className="text-emerald-600">{(int.modality_effectiveness * 100).toFixed(0)}% effective</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* LEI Measures */}
      <div className="bg-white rounded-xl border p-6">
        <h4 className="font-semibold text-gray-900 mb-4">LEI Measures to Track Impact</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {lei_measures.slice(0, 6).map((measure, idx) => (
            <div key={measure.id || measure.name || `measure-${idx}`} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{measure.name}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">{measure.pillar}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">{measure.pillar_name}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Calculation Details - Expandable Section */}
      <CalculationDetails results={results} />
      
      {/* Business-Case-Builder preview — shows what BCB will
          auto-generate so the user knows what they're about to land
          into when they click Export Business Case. */}
      <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/40 to-white p-4" data-testid="roi-bcb-preview">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={16} className="text-blue-600" />
          <span className="text-sm font-semibold text-slate-900">Your Business Case will include</span>
          <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded ml-auto">Auto-generated</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {BCB_DEFAULT_SECTIONS.map((s, i) => (
            <div key={s.title} className="rounded-md bg-white border border-slate-200 p-2.5">
              <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold text-blue-600">{i + 1}.</span>
                <span className="text-xs font-semibold text-slate-900 truncate">{s.title}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">{s.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
          <ChevronRight size={11} className="text-slate-400" />
          You can edit, reorder, disable sections, or apply AI enhancement after the case is generated.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={onCompare}
          disabled={comparing}
          data-testid="compare-scenarios-btn"
          className="flex items-center gap-2 px-6 py-3 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg font-medium disabled:opacity-50"
        >
          {comparing ? <Loader2 className="animate-spin" size={20} /> : <GitCompare size={20} />}
          Compare Scenarios
        </button>
        <div className="flex items-center gap-4">
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="save-solution-btn"
            className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
            Save Solution
          </button>
          <button
            onClick={onExport}
            data-testid="export-business-case-btn"
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            <Download size={20} />
            Export Business Case
          </button>
        </div>
      </div>
    </div>
  );
};
